// =============================================================================
// VandieZ shipper-web -- Support tickets (/support)
// =============================================================================
// Lists the shipper's tickets with a "Open a ticket" CTA + inline form.
// Per-ticket detail (messages thread + reply) lives at /support/:ticketId
// in SupportTicketDetailPage.
// =============================================================================

import { AlertCircle, HeadphonesIcon, Loader2, Plus } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { supportApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  TicketCategory,
  TicketRead,
  TicketStatus,
} from "@/types/support";

const CATEGORIES: { value: TicketCategory; label: string }[] = [
  { value: "trip", label: "Trip issue" },
  { value: "payment", label: "Payment" },
  { value: "kyc", label: "KYC / verification" },
  { value: "account", label: "Account" },
  { value: "other", label: "Other" },
];

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_TONE: Record<TicketStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  in_progress: "bg-primary-subtle text-primary-subtle-foreground",
  resolved: "bg-success-subtle text-success-subtle-foreground",
  closed: "bg-muted text-muted-foreground",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function SupportTicketsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [tickets, setTickets] = useState<TicketRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [showForm, setShowForm] = useState<boolean>(false);
  const [category, setCategory] = useState<TicketCategory>("trip");
  const [subject, setSubject] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  async function refresh(): Promise<void> {
    if (accessToken === null) {return;}
    try {
      setIsLoading(true);
      const res = await supportApi.list(accessToken, { limit: 100, offset: 0 });
      setTickets(res.items);
      setError(null);
    } catch (err: unknown) {
      setError(
        err instanceof ApiError ? err.problem.detail : "Couldn't load tickets.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (accessToken === null) {return;}
    if (subject.trim().length < 3 || body.trim().length < 10) {
      toast.error("Subject must be 3+ chars and message 10+.");
      return;
    }
    setIsSaving(true);
    try {
      await supportApi.create(accessToken, {
        category,
        subject: subject.trim(),
        body: body.trim(),
      });
      toast.success("Ticket created.");
      setSubject("");
      setBody("");
      setShowForm(false);
      await refresh();
    } catch (err: unknown) {
      toast.error(
        err instanceof ApiError ? err.problem.detail : "Couldn't create ticket.",
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support"
        description="Open a ticket for billing, KYC, trip issues, or account help."
        actions={
          <Button
            onClick={() => { setShowForm((v) => !v); }}
            variant={showForm ? "outline" : "default"}
          >
            <Plus className="h-4 w-4" />
            {showForm ? "Cancel" : "New ticket"}
          </Button>
        }
      />

      {error !== null ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {showForm ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">New ticket</CardTitle>
            <CardDescription>
              We respond within one business day. Urgent trip issues
              auto-escalate.
            </CardDescription>
          </CardHeader>
          <form onSubmit={(e) => void onSubmit(e)}>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={category}
                  onChange={(e) => { setCategory(e.target.value as TicketCategory); }}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  required
                  minLength={3}
                  maxLength={200}
                  value={subject}
                  onChange={(e) => { setSubject(e.target.value); }}
                  placeholder="Short summary"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="body">Message</Label>
                <Textarea
                  id="body"
                  required
                  minLength={10}
                  maxLength={4000}
                  rows={5}
                  value={body}
                  onChange={(e) => { setBody(e.target.value); }}
                  placeholder="Describe what happened, when, and any relevant trip / load IDs."
                />
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-6 pt-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowForm(false); }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Create ticket"
                )}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : tickets === null || tickets.length === 0 ? (
        <Card>
          <CardHeader className="items-center text-center">
            <HeadphonesIcon className="h-10 w-10 text-muted-foreground" />
            <CardTitle>No tickets yet</CardTitle>
            <CardDescription>
              Open a ticket when you need help — we keep the full thread
              right here for your records.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <Link
              key={t.id}
              to={`/support/${t.id}`}
              className="block transition-colors hover:opacity-95"
            >
              <Card className="cursor-pointer transition-colors hover:border-primary/50">
                <CardContent className="flex flex-wrap items-start justify-between gap-3 py-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                          STATUS_TONE[t.status],
                        )}
                      >
                        {STATUS_LABEL[t.status]}
                      </span>
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">
                        {t.category}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        · {t.message_count} message{t.message_count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <p className="mt-1 text-sm font-medium leading-snug">
                      {t.subject}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Opened {formatDate(t.created_at)} · Last activity{" "}
                      {formatDate(t.last_activity_at)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
